// The typed API surface. One function per backend capability, and no UI concepts anywhere.
//
// Types are hand-written rather than generated. The backend has no OpenAPI document and adding a
// generator would couple two build systems that currently share nothing; these shapes are small,
// stable, and a mismatch surfaces immediately in the one place that consumes them.
import { api } from './client.ts';

// ── identity ────────────────────────────────────────────────────────────────────────────────────

export interface WhoAmI {
  actorId: string;
  displayName: string;
  actorType: 'human' | 'service';
  authProvider: string;
  /** The RESOLVED permission set. The UI renders from this and never infers from a name or a team. */
  permissions: string[];
  /** Role codes the person actually holds, so the UI can say "planner" without deriving it. */
  roles: string[];
}

export const whoami = () => api.get<WhoAmI>('/whoami');
export const login = (username: string, password: string) =>
  api.post<{ actorId: string; displayName: string; csrfToken: string }>('/auth/login', { username, password });
export const logout = () => api.post<{ ok: true }>('/auth/logout');
export const logoutEverywhere = () => api.post<{ ok: true; revoked: number }>('/auth/logout-all');

/**
 * Capability checks, by permission and never by role name.
 *
 * A role is a bundle that somebody may re-cut tomorrow; a permission is the thing the database
 * actually enforces. Asking `is this person a planner` in the UI would be a second authorization model
 * that drifts from the real one — and would be wrong the moment a role gains or loses a permission.
 */
export const can = {
  plan: (me: WhoAmI | undefined) => !!me?.permissions.includes('planning.read'),
  assign: (me: WhoAmI | undefined) => !!me?.permissions.includes('planning.read'),
  seeOwn: (me: WhoAmI | undefined) => !!me?.permissions.includes('planning.read_own'),
};

// ── the planner read model ──────────────────────────────────────────────────────────────────────

export interface PlannerProject {
  projectId: number; projectCode: string;
  /** `2026-084` — what the company and the customer both call this job. */
  officialNumber?: string | null;
  numberKind?: 'order' | 'quote' | null;
  /** What people call this werf. NULL when nobody has named it — then the code IS the name. */
  projectName: string | null;
  customerName: string | null;
  phaseCount: number; workpackageCount: number;
  plannedStart: string | null; plannedEnd: string | null;
}

/**
 * The label a human recognises, and the label a system needs — in that order, never merged.
 *
 * "BS-644" is unreadable at a glance and it is also the only thing that is guaranteed unique, so both
 * appear everywhere: the name large, the code small underneath. One helper so the fallback rule
 * (no name → the code alone, never "null" or an empty line) is written once.
 */
export interface WerfLabel {
  /** Line 1: the place a human recognises. */
  name: string;
  /** Line 2, ready to print: "Project 2026-073", "Offerte 2026-073", or the internal code. */
  code: string | null;
  /** The bare number, for search and export. */
  number: string | null;
  kind: 'order' | 'quote' | 'internal' | null;
}

/**
 * THE ONE RESOLVER. Every screen calls this and none of them decides anything itself.
 *
 * ── WHY THE WORD IS PART OF THE ANSWER ──────────────────────────────────────────
 * BS-644's ORDER number is 2026073 and BS-650's QUOTE number is also 2026073 — two documents on two
 * werven that happen to share seven digits. Printed bare they are indistinguishable, so the word in
 * front is not decoration: it is what makes the identifier unambiguous. Deriving it in each screen
 * would be the same rule written six times, which is how five of them end up saying "Project".
 *
 * ── WHAT WINS ───────────────────────────────────────────────────────────────────
 * An order number when there is one — the work runs under it, and the quote stays attached but
 * secondary. Otherwise the quote. Otherwise the internal code, which is a key rather than a number
 * and is labelled as such by being shown alone.
 */
export const werfLabel = (p: {
  projectName?: string | null; projectCode: string;
  officialNumber?: string | null; numberKind?: 'order' | 'quote' | null;
}): WerfLabel => {
  const n = p.officialNumber ?? null;
  const kind = n ? (p.numberKind ?? 'order') : 'internal';
  const printed = n ? `${kind === 'quote' ? 'Offerte' : 'Project'} ${n}` : p.projectCode;
  if (p.projectName) return { name: p.projectName, code: printed, number: n, kind };
  // No name: the number becomes the headline and must not be printed twice.
  return { name: n ?? p.projectCode, code: null, number: n, kind };
};

export const listProjects = () =>
  api.get<{ projects: PlannerProject[] }>('/planning/projects').then((r) => r.projects);

export interface Person {
  actorId: string; displayName: string; employeeCode: string | null; workEmail: string | null;
  resourceId: number | null; defaultDailyMinutes: number | null;
}
export const listPeople = () =>
  api.get<{ people: Person[] }>('/planning/people').then((r) => r.people);

export interface Team {
  teamId: string; code: string; name: string;
  resourceId: number | null; defaultDailyMinutes: number | null;
  members: { actorId: string; displayName: string; roleInTeam: string | null }[];
}
export const listTeams = () =>
  api.get<{ teams: Team[] }>('/planning/teams').then((r) => r.teams);

/**
 * The crew, in the only shape the screen is allowed to know about.
 *
 * WHAT IS NOT IN THIS TYPE IS THE POINT. The source these people were entered from also records
 * personality notes, reliability judgements, language difficulties and income sensitivity. None of it
 * is on the wire, because `delivery.crew_for_planner()` cannot select it — so there is no field here to
 * accidentally render, and no future component can decide to show one.
 */
export interface CrewMember {
  actorId: string;
  displayName: string;
  /** Ploeg name, or null for somebody in no team. */
  team: string | null;
  /** Named only for people from another firm. Null for own staff — never "eigen personeel" as a label. */
  externalOrg: string | null;
  /** False for the operational lead: they plan, they are not a pair of hands on a werf. */
  fieldCapacity: boolean;
  /** Neutral and work-facing: this person is not booked alone. It says nothing about why. */
  needsSupervision: boolean;
  maySupervise: boolean;
  /** Trades at `zelfstandig` or better. A capability list, never a ranking. */
  skills: string[];
}
export const listCrew = () =>
  api.get<{ crew: CrewMember[] }>('/planning/crew').then((r) => r.crew);

/**
 * Discipline codes as Dutch, for the two views that only receive the CODE.
 *
 * `delivery.discipline.name` already holds these words and is the authority; the schedule endpoint
 * carries the code alone, and widening it would be a backend change the UI can do without. A code this
 * map does not know falls through unchanged — the same degradation as before, never a blank.
 */
const DISCIPLINE_LABELS: Record<string, string> = {
  heating: 'Verwarming', cooling: 'Koeling', ventilation: 'Ventilatie',
  sanitary: 'Sanitair', electrical: 'Elektriciteit', home_automation: 'Domotica',
};
export const disciplineLabel = (c: string): string => DISCIPLINE_LABELS[c] ?? c;

/**
 * Domain codes as a planner says them out loud.
 *
 * THE MAP IS FOR THE ONES WHERE THE CODE IS NOT THE WORD, and nothing else. The register holds far
 * more domains than a fixed list could track — `niko_home_control`, `dali_drivers`,
 * `hydraulische_plannen`, and whatever the next werf adds — so anything unlisted has its underscores
 * turned into spaces and is shown as written. That degrades to "readable", never to a blank and never
 * to a raw identifier with underscores in it.
 */
const TRADE_LABELS: Record<string, string> = {
  koeltechniek: 'koeling',
  unifi_netwerk: 'UniFi-netwerk',
  niko_home_control: 'Niko Home Control',
  knx: 'KNX',
  dali_drivers: 'DALI',
  cv: 'CV',
};
export const tradeLabel = (d: string): string => TRADE_LABELS[d] ?? d.replace(/_/g, ' ');

/**
 * The first few trades, plus how many are not shown.
 *
 * The operational lead carries twelve. Printed in full they are a paragraph under a name in a 170px
 * lane, which reads as noise and pushes the thing a planner actually came for off the screen. The
 * server already returns them best-first, so the first three are the useful ones and the count keeps
 * the line honest about what it left out.
 */
export const tradeSummary = (skills: string[], max = 3): { shown: string; hidden: number; full: string } => ({
  shown: skills.slice(0, max).map(tradeLabel).join(', '),
  hidden: Math.max(0, skills.length - max),
  full: skills.map(tradeLabel).join(', '),
});

export interface ScheduleItem {
  day: string; resourceId: number; actorId: string | null; teamId: string | null;
  resourceName: string | null; isTeam: boolean;
  assignmentId: number;
  /**
   * WHICH DAY-ROW this chip is, when there is one.
   *
   * NULL means the day is still part of an even spread and has no row yet — the first drag creates
   * one. Either way the chip drags by (assignmentId, day), so the UI never branches on it.
   */
  allocationId?: number | null;
  /** This day's SLICE of the assignment — the number one calendar cell shows. */
  minutes: number;
  /**
   * The assignment's WHOLE allocation.
   *
   * NOT interchangeable with `minutes`, and treating them as one is how a reassignment used to shrink
   * the job: moving somebody is supersede-then-assign, and the new assignment was created for the
   * daily slice instead of the total. Summing `minutes` client-side cannot substitute — the fetched
   * window may not contain the whole assignment.
   */
  allocatedMinutes: number;
  projectId: number; projectCode: string; projectName: string | null; customerName: string | null;
  /** `2026-084`. Rendered under the werf name on every chip and card. */
  officialNumber?: string | null;
  /** Whether that number is an order or a quote — see `werfLabel` for why the word matters. */
  numberKind?: 'order' | 'quote' | null;
  workpackageId: number; title: string;
  /** The planner's own note on this work. Edited in the side panel. */
  scope: string | null;
  lifecycleState: string; disciplineCode: string | null;
  plannedStart: string | null; plannedEnd: string | null;
  /** Carried on every item so a drag can send it back as `expectedRevision` without a second fetch. */
  planRevision: number;
}
export interface Schedule {
  from: string; to: string;
  items: ScheduleItem[];
  /** Minutes from projects this viewer cannot see. Rendered as a grey bar, never attributed. */
  otherCommitments: { day: string; resourceId: number; minutes: number }[];
  capacity: { day: string; resourceId: number; capacityMinutes: number }[];
}

export const getSchedule = (from: string, to: string, actorId?: string) =>
  api.get<Schedule>(`/planning/schedule?from=${from}&to=${to}${actorId ? `&actorId=${actorId}` : ''}`);
export const getMySchedule = (from: string, to: string) =>
  api.get<Schedule>(`/planning/my-schedule?from=${from}&to=${to}`);

// ── the project plan ────────────────────────────────────────────────────────────────────────────

export interface Phase {
  id: number; phaseTemplateId: string; templateName?: string; status: string;
  plannedStart: string | null; plannedEnd: string | null; planRevision: number;
}
export interface Workpackage {
  id: number; projectPhaseId: number; disciplineId: string; disciplineCode: string | null; title: string;
  lifecycleState: string; necessity: string;
  plannedStart: string | null; plannedEnd: string | null; planRevision: number;
  ownerActorId: string | null; ownerTeamId: string | null;
}
export interface Conflict {
  code: string; subjectType: string; subjectId: number; cause: number | string | null; detail: string;
}

/**
 * A DATE column, as these older endpoints actually serialise it.
 *
 * They return the driver's `Date` object, so `2026-08-06` leaves the server as
 * `"2026-08-05T22:00:00.000Z"` — midnight local at UTC+2. Taking `.slice(0, 10)` off that string, the
 * obvious thing to write, yields 2026-08-05 and shows every window ONE DAY EARLY. Parsed and
 * reformatted from LOCAL components instead, which is the same rule `lib/dates.ts` states for the
 * other direction.
 *
 * `/planning/schedule` does not need this: it casts to `::text` in SQL and hands back a real
 * `YYYY-MM-DD`. These two predate that and are normalised here rather than changed server-side —
 * touching a Slice F endpoint would mean a backend change the UI does not actually need.
 */
const toYmd = (v: string | null): string | null => {
  if (!v) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * THESE TWO ENDPOINTS SPEAK snake_case. Slices F and G return database column names verbatim while the
 * newer planner routes return camelCase. Rather than let that split leak into every component, both
 * are normalised here — the API client is exactly the layer whose job is to make the wire look like
 * the app's own vocabulary.
 */
interface PhaseWire {
  id: number; phase_template_id: string; template_name?: string; template_code?: string; status: string;
  planned_start: string | null; planned_end: string | null; plan_revision: number;
}
interface WorkpackageWire {
  id: number; project_phase_id: number; discipline_id: string; discipline_code?: string; title: string;
  lifecycle_state: string; necessity: string;
  planned_start: string | null; planned_end: string | null; plan_revision: number;
  owner_actor_id: string | null; owner_team_id: string | null;
}

export const getPhases = (projectId: number): Promise<Phase[]> =>
  api.get<PhaseWire[]>(`/projects/${projectId}/delivery/phases`).then((rows) => rows.map((r) => ({
    id: r.id,
    phaseTemplateId: r.phase_template_id,
    templateName: r.template_name ?? r.template_code,
    status: r.status,
    plannedStart: toYmd(r.planned_start),
    plannedEnd: toYmd(r.planned_end),
    planRevision: Number(r.plan_revision ?? 0),
  })));

export const getWorkpackages = (projectId: number): Promise<Workpackage[]> =>
  api.get<WorkpackageWire[]>(`/projects/${projectId}/delivery/workpackages`).then((rows) => rows.map((r) => ({
    id: r.id,
    projectPhaseId: r.project_phase_id,
    disciplineId: r.discipline_id,
    disciplineCode: r.discipline_code ?? null,
    title: r.title,
    lifecycleState: r.lifecycle_state,
    necessity: r.necessity,
    plannedStart: toYmd(r.planned_start),
    plannedEnd: toYmd(r.planned_end),
    planRevision: Number(r.plan_revision ?? 0),
    ownerActorId: r.owner_actor_id,
    ownerTeamId: r.owner_team_id,
  })));
export const getConflicts = (projectId: number) =>
  api.get<{ blocking: boolean; conflicts: Conflict[]; reasons: string[] }>(
    `/projects/${projectId}/delivery/conflicts`);
export const getCapacity = (projectId: number) =>
  api.get<{
    conflicts: Conflict[];
    utilisation: { resourceId: number; day: string; capacityHours: number; assignedHours: number; freeHours: number; overloadHours: number }[];
    generations: { resourceId: number; generation: number }[];
  }>(`/projects/${projectId}/delivery/capacity`);
export interface ProjectAssignment {
  id: number; workpackageId: number; resourceId: number; resourceName: string | null;
  allocatedHours: number; supersededAt: string | null;
}
export const getAssignments = (projectId: number): Promise<ProjectAssignment[]> =>
  api.get<Record<string, unknown>[]>(`/projects/${projectId}/delivery/assignments`).then((rows) => rows.map((r) => ({
    // Tolerant of BOTH shapes: this route is camelCase today, its neighbours are not, and a reader
    // that only understands one of them breaks silently the day either side is touched.
    id: Number(r.id),
    workpackageId: Number(r.workpackageId ?? r.workpackage_id),
    resourceId: Number(r.resourceId ?? r.resource_id),
    resourceName: (r.resourceName ?? r.resource_name ?? null) as string | null,
    allocatedHours: Number(r.allocatedHours ?? r.allocated_hours ?? 0),
    supersededAt: (r.supersededAt ?? r.superseded_at ?? null) as string | null,
  })));
export const getProjectResources = (projectId: number) =>
  api.get<{ id: number; actorId: string | null; teamId: string | null; displayName: string | null; defaultDailyHours: number }[]>(
    `/projects/${projectId}/delivery/resources`);

// ── the guarded writes ──────────────────────────────────────────────────────────────────────────

/**
 * Move a workpackage's window.
 *
 * `expectedRevision` IS NOT OPTIONAL HERE even though the server accepts null. Sending null disables
 * the optimistic-lock check and silently overwrites whoever moved it last — so the client type makes
 * it required and every caller passes the revision it actually rendered.
 */
export const setWorkpackageWindow = (
  projectId: number, workpackageId: number,
  input: { start: string; end: string; reason: string; expectedRevision: number; correlationId: string },
) => api.put<{ revision: number; idempotent: boolean }>(
  `/projects/${projectId}/delivery/workpackages/${workpackageId}/plan`,
  // THE WIRE NAMES ARE `plannedStart`/`plannedEnd` and the body is `.strict()`, so a near-miss like
  // `start`/`end` is rejected wholesale rather than partially applied. Found by driving the real API
  // end to end rather than by reading the schema — which is why that test exists.
  {
    plannedStart: input.start, plannedEnd: input.end, reason: input.reason,
    expectedRevision: input.expectedRevision,
  },
  input.correlationId,
);

export const assign = (
  projectId: number,
  input: { workpackageId: number; resourceId: number; allocatedHours: number; reason: string; correlationId: string },
) => api.post<{ id: number; idempotent: boolean }>(
  `/projects/${projectId}/delivery/assignments`,
  {
    workpackageId: input.workpackageId, resourceId: input.resourceId,
    allocatedHours: input.allocatedHours, reason: input.reason,
  },
  input.correlationId,
);

export const unassign = (projectId: number, assignmentId: number, correlationId: string) =>
  api.del<{ superseded: true }>(`/projects/${projectId}/delivery/assignments/${assignmentId}`, correlationId);

/**
 * Rename a workpackage, or save the planner's note on it.
 *
 * TITLE AND NOTE ONLY. The window goes through `setWorkpackageWindow` and the person through
 * `assign`/`unassign` — three different authorities, three different audit trails, and the server
 * rejects a window sent here outright rather than ignoring it.
 */
export const editWorkpackage = (
  projectId: number, workpackageId: number,
  input: { title?: string; scope?: string | null; disciplineId?: string; category?: string; correlationId: string },
) => api.patch<{ ok: true }>(
  `/projects/${projectId}/delivery/workpackages/${workpackageId}`,
  {
    ...(input.title !== undefined ? { title: input.title } : {}),
    ...(input.scope !== undefined ? { scope: input.scope } : {}),
    ...(input.disciplineId !== undefined ? { disciplineId: input.disciplineId } : {}),
    ...(input.category !== undefined ? { category: input.category } : {}),
  },
  input.correlationId,
);

/** Name a werf. Empty clears the name and the code becomes the label again. */
export const renameProject = (projectId: number, displayName: string | null, correlationId: string) =>
  api.patch<{ ok: true }>(`/projects/${projectId}`, { displayName }, correlationId);

/** Create a workpackage. The "nieuwe opdracht" flow, which had no interface at all before MVP 1.0. */
export const createWorkpackage = (projectId: number, input: {
  projectPhaseId: number; disciplineId: string; title: string; scope?: string | null;
  correlationId: string;
}) => api.post<{ id: number }>(
  `/projects/${projectId}/delivery/workpackages`,
  {
    projectPhaseId: input.projectPhaseId, disciplineId: input.disciplineId, title: input.title,
    ...(input.scope ? { scope: input.scope } : {}),
  },
  input.correlationId,
);

export interface Discipline { id: string; code: string; name: string; active: boolean }
export const getDisciplines = (projectId: number) =>
  api.get<Discipline[]>(`/projects/${projectId}/delivery/disciplines`);

/** Move a workpackage through its lifecycle. The database decides which moves this caller may make. */
export const transitionWorkpackage = (
  projectId: number, workpackageId: number,
  input: { toState: string; reason?: string | null; correlationId: string },
) => api.post<{ eventId: number; fromState: string; toState: string; idempotent: boolean }>(
  `/projects/${projectId}/delivery/workpackages/${workpackageId}/transition`,
  { toState: input.toState, ...(input.reason ? { reason: input.reason } : {}) },
  input.correlationId,
);

/**
 * WHICH MOVES ARE LEGAL, ASKED OF THE DATABASE RATHER THAN GUESSED.
 *
 * The matrix lives in `delivery.lifecycle_transition` and is enforced inside
 * `delivery.transition_workpackage`. The first version of the panel offered all seven states, so
 * picking "Ingepland" on a fresh workpackage produced "transition draft → planned is not allowed" —
 * a control that could never work, in a form that had just saved three other fields. Reproducing the
 * matrix in TypeScript would be a second copy of a rule that changes; fetching it is one request,
 * cached, and always right.
 */
export interface LifecycleEdge {
  from_state: string; to_state: string; requires_permission: string; requires_reason: boolean;
}
export const getLifecycle = (projectId: number) =>
  api.get<LifecycleEdge[]>(`/projects/${projectId}/delivery/lifecycle`);

/** The states a planner can reach from the panel, in the order work actually moves through them. */
export const LIFECYCLE: { value: string; label: string }[] = [
  { value: 'draft', label: 'Voorstel' },
  { value: 'ready', label: 'Klaar om in te plannen' },
  { value: 'planned', label: 'Ingepland' },
  { value: 'in_progress', label: 'Bezig' },
  { value: 'blocked', label: 'Geblokkeerd' },
  { value: 'completed', label: 'Afgewerkt' },
  // Both of these are reachable from the matrix, so both need a Dutch label — `waived` appeared raw in
  // the dropdown because the list below was written from the states a planner USUALLY picks rather
  // than from the states the database actually offers.
  { value: 'waived', label: 'Komt te vervallen' },
  { value: 'cancelled', label: 'Geannuleerd' },
  { value: 'superseded', label: 'Vervangen' },
];
export const lifecycleLabel = (s: string): string =>
  LIFECYCLE.find((l) => l.value === s)?.label ?? s;


// ── sprint 3: choosing a werf, planning a day, the decision log ─────────────────

export interface ProjectChoice {
  projectId: number; projectCode: string;
  displayName: string | null; officialNumber: string | null;
  customerName: string | null; address: string | null;
  status: string | null; inPlanner: boolean; confidence: string;
  /** 'order' or 'quote' — the same digits can be an order on one werf and a quote on another. */
  numberKind?: 'order' | 'quote' | null;
}
export const searchProjects = (q: string) =>
  api.get<{ projects: ProjectChoice[] }>(`/planning/project-search?q=${encodeURIComponent(q)}`)
    .then((r) => r.projects);

export const admitProject = (projectId: number, reason: string, correlationId: string) =>
  api.post<{ ok: true }>('/planning/admit-project', { projectId, reason }, correlationId);

export interface DayAllocation {
  id: number; assignmentId: number; workpackageId: number; resourceId: number;
  day: string; minutes: number; note: string | null; status: string;
}
export const getAllocations = (projectId: number, workpackageId: number) =>
  api.get<{ allocations: DayAllocation[] }>(
    `/projects/${projectId}/delivery/allocations?workpackageId=${workpackageId}`).then((r) => r.allocations);

/** Set the hours one person works on one job on one day. `minutes: 0` clears the day. */
export const setAllocation = (projectId: number, input: {
  assignmentId: number; day: string; minutes: number; note?: string | null;
  before?: number | null; screen?: 'month' | 'week' | 'day' | 'project';
  source?: 'manual' | 'drag' | 'bulk' | 'copy'; correlationId: string;
}) => api.put<{ id: number | null }>(`/projects/${projectId}/delivery/allocations`, {
  assignmentId: input.assignmentId, day: input.day, minutes: input.minutes,
  ...(input.note !== undefined ? { note: input.note } : {}),
  ...(input.before !== undefined ? { before: input.before } : {}),
  ...(input.screen ? { screen: input.screen } : {}),
  ...(input.source ? { source: input.source } : {}),
}, input.correlationId);

/**
 * SUPERSEDED by `moveAllocation` below, which keys on (assignment, day).
 * Kept because the route still exists and is still tested; nothing in the UI calls it.
 */
export const moveAllocationById = (projectId: number, allocationId: number, input: {
  toDay: string; fromDay?: string | null; screen?: 'month' | 'week' | 'day' | 'project';
  correlationId: string;
}) => api.post<{ ok: true }>(`/projects/${projectId}/delivery/allocations/${allocationId}/move`, {
  toDay: input.toDay,
  ...(input.fromDay ? { fromDay: input.fromDay } : {}),
  ...(input.screen ? { screen: input.screen } : {}),
}, input.correlationId);

/**
 * Record a decision the server cannot see for itself — an undo, a copy, an override.
 *
 * FIRE AND FORGET, DELIBERATELY. A failed event must never surface to the planner or block what they
 * were doing: the log is a side effect of the decision, not the decision. Errors are swallowed here
 * and on the server for the same reason.
 */
export const recordEvent = (e: {
  eventType: string; projectId?: number | null; workpackageId?: number | null;
  allocationId?: number | null; resourceId?: number | null;
  disciplineCode?: string | null; taskCategory?: string | null; projectNumber?: string | null;
  correlationId?: string | null; reason?: string | null;
  source?: 'manual' | 'drag' | 'bulk' | 'copy' | 'suggestion' | 'import';
  screen?: 'month' | 'week' | 'day' | 'project' | null;
  accepted?: boolean; rejectionCode?: string | null;
  valueBefore?: Record<string, string | number | boolean | null> | null;
  valueAfter?: Record<string, string | number | boolean | null> | null;
}): void => {
  void api.post('/planning/events', e).catch(() => {});
};

export interface HistoryFilter {
  projectId?: number; workpackageId?: number; resourceId?: number;
  from?: string; to?: string; eventType?: string; limit?: number;
}

/** The event types a planner would actually filter on, in the order work moves through them. */
export const EVENT_TYPES: { value: string; label: string }[] = [
  { value: 'allocation_created', label: 'Dag ingepland' },
  { value: 'allocation_hours_changed', label: 'Uren gewijzigd' },
  { value: 'allocation_date_changed', label: 'Verplaatst naar andere dag' },
  { value: 'allocation_resource_changed', label: 'Andere medewerker' },
  { value: 'allocation_split', label: 'Opgesplitst' },
  { value: 'allocation_copied', label: 'Gekopieerd' },
  { value: 'allocation_deleted', label: 'Dag weggehaald' },
  { value: 'assignment_added', label: 'Medewerker toegevoegd' },
  { value: 'assignment_removed', label: 'Medewerker weggehaald' },
  { value: 'workpackage_renamed', label: 'Taak hernoemd' },
  { value: 'task_category_changed', label: 'Soort werk gewijzigd' },
  { value: 'status_changed', label: 'Status gewijzigd' },
  { value: 'project_added_to_planner', label: 'Werf toegevoegd' },
  { value: 'undo_performed', label: 'Ongedaan gemaakt' },
  { value: 'planner_suggestion_rejected', label: 'Geweigerd' },
];

export interface HistoryEntry {
  id: number; occurredAt: string; actorName: string | null; eventType: string;
  projectNumber?: string | null; workpackageTitle?: string | null;
  valueBefore: Record<string, unknown> | null; valueAfter: Record<string, unknown> | null;
  reason: string | null; source: string; accepted: boolean; resourceName: string | null;
}
export const getHistory = (f: HistoryFilter) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') q.set(k, String(v));
  return api.get<{ history: HistoryEntry[] }>(`/planning/history?${q}`).then((r) => r.history);
};

/** The seven kinds of hours a planner distinguishes. */
export const CATEGORIES = [
  'voorbereiding', 'ontwerp', 'uitvoering', 'werfopvolging',
  'indienststelling', 'controle', 'administratie',
] as const;

/**
 * Move ONE DAY of work: to another date, another person, or both.
 *
 * Keyed by (assignment, day) and not by allocation id, because the day being dragged may still be
 * part of an even spread and have no row at all. The database materialises it.
 */
export const moveAllocation = (projectId: number, input: {
  assignmentId: number; day: string; toDay?: string; toResourceId?: number;
  screen?: 'month' | 'week' | 'day' | 'project'; correlationId: string;
}) => api.post<{ assignmentId: number; day: string }>(
  `/projects/${projectId}/delivery/allocations/move`,
  {
    assignmentId: input.assignmentId, day: input.day,
    ...(input.toDay ? { toDay: input.toDay } : {}),
    ...(input.toResourceId ? { toResourceId: input.toResourceId } : {}),
    ...(input.screen ? { screen: input.screen } : {}),
  }, input.correlationId);

export const splitAllocation = (projectId: number, input: {
  assignmentId: number; day: string; keepMinutes: number; toDay: string;
  toResourceId?: number | null; note?: string | null;
  screen?: 'month' | 'week' | 'day' | 'project'; correlationId: string;
}) => api.post<{ ok: true }>(`/projects/${projectId}/delivery/allocations/split`, {
  assignmentId: input.assignmentId, day: input.day,
  keepMinutes: input.keepMinutes, toDay: input.toDay,
  ...(input.toResourceId ? { toResourceId: input.toResourceId } : {}),
  ...(input.note ? { note: input.note } : {}),
  ...(input.screen ? { screen: input.screen } : {}),
}, input.correlationId);

export interface Budget { estimatedMinutes: number; plannedMinutes: number; remainingMinutes: number }
export const getBudget = (projectId: number, workpackageId: number) =>
  api.get<Budget>(`/projects/${projectId}/delivery/budget?workpackageId=${workpackageId}`);
